import { supabase, BlogPost, SearchLog } from '../lib/supabase';
import { GoogleGenAI } from '@google/genai';
import type { LaptopComparison } from '../types';

const genAI = new GoogleGenAI({ apiKey: import.meta.env.VITE_GEMINI_API_KEY || '' });

/**
 * Normalize search query to detect similar searches
 * Converts to lowercase, removes extra spaces, sorts words
 */
function normalizeQuery(query: string): string {
  return query
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ')
    .split(' ')
    .filter(word => word.length > 2) // Remove short words
    .sort()
    .join(' ');
}

/**
 * Check if a similar blog post already exists for this search query
 */
export async function checkExistingBlogPost(searchQuery: string): Promise<string | null> {
  const normalized = normalizeQuery(searchQuery);
  
  try {
    const { data, error } = await supabase
      .from('search_logs')
      .select('blog_post_id')
      .eq('normalized_query', normalized)
      .not('blog_post_id', 'is', null)
      .limit(1)
      .single();

    if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
      console.error('Error checking existing blog post:', error);
      return null;
    }

    return data?.blog_post_id || null;
  } catch (error) {
    console.error('Error in checkExistingBlogPost:', error);
    return null;
  }
}

/**
 * Log search query to database
 */
export async function logSearchQuery(
  searchQuery: string, 
  resultCount: number, 
  blogPostId: string | null = null
): Promise<void> {
  const normalized = normalizeQuery(searchQuery);
  
  try {
    const { error } = await supabase
      .from('search_logs')
      .insert({
        search_query: searchQuery,
        normalized_query: normalized,
        result_count: resultCount,
        blog_post_id: blogPostId
      });

    if (error) {
      console.error('Error logging search query:', error);
    }
  } catch (error) {
    console.error('Error in logSearchQuery:', error);
  }
}

/**
 * Generate blog post content using Gemini AI
 */
async function generateBlogContent(
  searchQuery: string,
  laptops: LaptopComparison[]
): Promise<{ title: string; content: string; excerpt: string }> {
  const model = genAI.getGenerativeModel({ model: 'gemini-1.5-flash' });

  const laptopSummaries = laptops.slice(0, 5).map((laptop, index) => 
    `${index + 1}. ${laptop.brand} ${laptop.model}
    - Price: ₹${laptop.price?.toLocaleString('en-IN') || 'N/A'}
    - Rating: ${laptop.rating || 'N/A'}/5
    - Summary: ${laptop.summary}
    - Key Specs: ${laptop.specs.Processor || 'N/A'}, ${laptop.specs.RAM || 'N/A'}, ${laptop.specs.Graphics || 'N/A'}`
  ).join('\n\n');

  const prompt = `You are a tech writer for CompareLens, an AI-powered laptop comparison platform. 
Write a comprehensive, engaging blog post about: "${searchQuery}"

Based on these laptop comparison results:
${laptopSummaries}

Requirements:
1. Create a catchy, SEO-friendly title
2. Write an engaging introduction (2-3 paragraphs)
3. Include detailed comparison of top laptops
4. Add buying guide and recommendations
5. Conclude with final thoughts
6. Use markdown formatting (headings, lists, bold, etc.)
7. Make it informative, helpful, and easy to read
8. Length: 800-1200 words
9. Include specific model names and prices

Also provide:
- A compelling 150-character excerpt for social sharing

Format your response as JSON:
{
  "title": "Blog post title",
  "excerpt": "Short excerpt",
  "content": "Full blog content in markdown"
}`;

  try {
    const result = await model.generateContent(prompt);
    const text = result.response.text();
    
    // Extract JSON from response
    const jsonMatch = text.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      const parsed = JSON.parse(jsonMatch[0]);
      return {
        title: parsed.title,
        content: parsed.content,
        excerpt: parsed.excerpt
      };
    }
    
    throw new Error('Failed to parse AI response');
  } catch (error) {
    console.error('Error generating blog content:', error);
    
    // Fallback content
    return {
      title: `Best Laptops for ${searchQuery}`,
      excerpt: `Discover the top laptop options for ${searchQuery}. Compare specs, prices, and features to find your perfect match.`,
      content: `# Best Laptops for ${searchQuery}\n\nBased on our AI-powered analysis, here are the top laptop recommendations:\n\n${laptopSummaries}`
    };
  }
}

/**
 * Create slug from title
 */
function createSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Create a blog post from search results
 */
export async function createBlogPostFromSearch(
  searchQuery: string,
  laptops: LaptopComparison[]
): Promise<string | null> {
  try {
    // Check if blog post already exists
    const existingBlogId = await checkExistingBlogPost(searchQuery);
    if (existingBlogId) {
      console.log('Blog post already exists for this query:', existingBlogId);
      await logSearchQuery(searchQuery, laptops.length, existingBlogId);
      return existingBlogId;
    }

    // Generate blog content
    const { title, content, excerpt } = await generateBlogContent(searchQuery, laptops);
    const slug = createSlug(title);
    
    // Extract laptop names
    const laptopsMentioned = laptops.slice(0, 5).map(l => `${l.brand} ${l.model}`);

    // Insert blog post
    const { data, error } = await supabase
      .from('blog_posts')
      .insert({
        title,
        slug,
        search_query: searchQuery,
        content,
        excerpt,
        laptops_mentioned: laptopsMentioned,
        published: true,
        view_count: 0
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating blog post:', error);
      return null;
    }

    // Log the search with blog post ID
    await logSearchQuery(searchQuery, laptops.length, data.id);

    console.log('Blog post created successfully:', data.id);
    return data.id;
  } catch (error) {
    console.error('Error in createBlogPostFromSearch:', error);
    return null;
  }
}

/**
 * Get all published blog posts
 */
export async function getAllBlogPosts(limit: number = 20): Promise<BlogPost[]> {
  try {
    const { data, error } = await supabase
      .from('blog_posts')
      .select('*')
      .eq('published', true)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching blog posts:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error in getAllBlogPosts:', error);
    return [];
  }
}

/**
 * Get blog post by slug
 */
export async function getBlogPostBySlug(slug: string): Promise<BlogPost | null> {
  try {
    const { data, error } = await supabase
      .from('blog_posts')
      .select('*')
      .eq('slug', slug)
      .eq('published', true)
      .single();

    if (error) {
      console.error('Error fetching blog post:', error);
      return null;
    }

    // Increment view count
    if (data) {
      await supabase
        .from('blog_posts')
        .update({ view_count: data.view_count + 1 })
        .eq('id', data.id);
    }

    return data;
  } catch (error) {
    console.error('Error in getBlogPostBySlug:', error);
    return null;
  }
}

/**
 * Get recent blog posts for blog page
 */
export async function getRecentBlogPosts(limit: number = 10): Promise<BlogPost[]> {
  return getAllBlogPosts(limit);
}
